import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch, apiUpload, apiDownload } from '../api';
import { formatDateTime } from '../utils/dateFormat';
import { periodToMmYyyy, normalizePeriodInput, formatPeriodsInText } from '../periodFormat';
import UploadRouteConfirm from '../components/UploadRouteConfirm';
import {
  detectUploadDestination,
  destinationMatchesTab,
  UPLOAD_PAGE_BY_DEST,
} from '../utils/uploadDestination';
import { setPendingUpload, takePendingUpload } from '../utils/pendingUpload';
import {
  UploadPageShell,
  UploadDropZone,
  UploadAlert,
  UploadRecordPreviewModal,
  UploadListToolbar,
  UploadRowList,
} from '../components/UploadPageLayout';
import { commissionUploadExportPath, exportUploadFile } from '../utils/exportUpload';
import { statementSearchText } from '../utils/statementDisplayName';
import { bsiUploadTitle, compareBsiUploadsByTitle } from '../bsiStatementTitle';

function fmt(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Chargebacks that exceed what was paid (or with no payment found) — to dispute with the carrier. */
function ChargebackFlagTable({ rows }) {
  if (!rows || !rows.length) return null;
  const th = { textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
  const td = { padding: '4px 6px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' };
  return (
    <div style={{ overflowX: 'auto', marginTop: 6, maxHeight: 320, overflowY: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
        <thead>
          <tr>
            <th style={th}>Status</th><th style={th}>Carrier</th><th style={th}>Client</th>
            <th style={th}>Policy</th><th style={th}>Effective</th><th style={th}>Period</th>
            <th style={th}>Agent</th><th style={{ ...th, textAlign: 'right' }}>Chargeback</th>
            <th style={{ ...th, textAlign: 'right' }}>Total paid</th>
            <th style={{ ...th, textAlign: 'right' }}>Total charged back</th>
            <th style={{ ...th, textAlign: 'right' }}>Over paid</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i}>
              <td style={td}>{r.status === 'no_payment' ? 'No payment found' : 'Exceeds paid'}</td>
              <td style={td}>{r.carrier}</td><td style={td}>{r.client}</td><td style={td}>{r.policy}</td>
              <td style={td}>{r.effectiveDate}</td><td style={td}>{periodToMmYyyy(r.period) || r.period}</td><td style={td}>{r.agent}</td>
              <td style={{ ...td, textAlign: 'right' }}>{fmt(r.amount)}</td>
              <td style={{ ...td, textAlign: 'right' }}>{fmt(r.paid)}</td>
              <td style={{ ...td, textAlign: 'right' }}>{fmt(r.chargedBack)}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmt(r.excess)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChargebackCheckPanel() {
  const [period, setPeriod] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const periodKey = normalizePeriodInput(period);
  const qs = /^\d{6}$/.test(periodKey) ? `?period=${periodKey}` : '';
  const run = async () => {
    setBusy(true); setErr('');
    try { setData(await apiFetch(`/records/chargeback-paid-check${qs}`)); }
    catch (e) { setErr(e.message || 'Check failed'); }
    finally { setBusy(false); }
  };
  const exportXlsx = async () => {
    try {
      await apiDownload(`/records/chargeback-paid-check${qs ? qs + '&' : '?'}format=xlsx`,
        `chargebacks_over_paid_${qs ? periodKey : 'all'}.xlsx`);
    } catch (e) { setErr(e.message || 'Export failed'); }
  };
  return (
    <div style={{ margin: '12px 0', padding: 12, border: '1px solid #e5e7eb', borderRadius: 8 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Chargebacks vs. paid</div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
        Chargebacks that are more than the carrier paid on the policy (all BSI uploads), or with no payment found.
      </div>
      <input
        value={period}
        onChange={(e) => setPeriod(e.target.value.trim())}
        placeholder="Month MM.YYYY (blank = all)"
        style={{ padding: '4px 8px', marginRight: 8, width: 190 }}
      />
      <button type="button" onClick={run} disabled={busy}>{busy ? 'Checking…' : 'Check'}</button>{' '}
      <button type="button" onClick={exportXlsx} disabled={busy}>Export</button>
      {err && <div style={{ color: '#b91c1c', marginTop: 6 }}>{err}</div>}
      {data && (
        <div style={{ marginTop: 8 }}>
          {data.summary
            ? <div style={{ fontWeight: 600, color: '#b45309' }}>⚠ {formatPeriodsInText(data.summary.message)}</div>
            : <div>No chargebacks over what was paid.</div>}
          <ChargebackFlagTable rows={data.rows} />
        </div>
      )}
    </div>
  );
}

export default function BSIStatementsUpload({ user, onNavigate }) {
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [routeConfirm, setRouteConfirm] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCarrier, setFilterCarrier] = useState('');
  const [sortBy, setSortBy] = useState('date_desc'); // date_desc | date_asc | carrier
  const [selectedUploads, setSelectedUploads] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Record preview — shared React modal, replacing the old DOM innerHTML
  // modal below (unescaped field interpolation, no React event handling).
  const [viewUpload, setViewUpload] = useState(null);
  const [viewRecords, setViewRecords] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);

  const loadUploads = useCallback(async () => {
    try {
      const data = await apiFetch('/files/uploads?category=bsi_statement');
      setUploads(data || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadUploads();
  }, [loadUploads]);

  useEffect(() => {
    const pending = takePendingUpload();
    if (pending) queueFile(pending);
  }, []);

  function queueFile(file) {
    if (!file) return;
    const detected = detectUploadDestination(file.name);
    if (!destinationMatchesTab(detected.id, 'bsi_statement') && detected.confidence !== 'low') {
      setRouteConfirm({ file, detected });
      return;
    }
    handleFile(file);
  }

  async function handleFile(file) {
    if (!file) return;
    setUploading(true);
    setError('');
    setUploadResult(null);

    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'bsi_statement');

      const result = await apiUpload('/files/upload-bsi-statement', fd);
      setUploadResult(result);
      loadUploads();
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function deleteUpload(id) {
    if (!window.confirm('Delete this BSI statement upload?')) return;
    setDeletingId(id);
    try {
      await apiFetch(`/files/uploads/${id}`, { method: 'DELETE' });
      setUploads(prev => prev.filter(u => u.id !== id));
    } catch (e) {
      console.error(e);
    } finally {
      setDeletingId(null);
    }
  }

  function toggleSelectUpload(id) {
    setSelectedUploads((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  function selectAllUploads() {
    if (selectedUploads.size === visibleUploads.length) setSelectedUploads(new Set());
    else setSelectedUploads(new Set(visibleUploads.map((u) => u.id)));
  }

  async function bulkDeleteUploads() {
    if (!window.confirm(`Delete ${selectedUploads.size} BSI statement uploads and all their records?`)) return;
    setBulkDeleting(true);
    try {
      for (const id of selectedUploads) {
        await apiFetch(`/files/uploads/${id}`, { method: 'DELETE' });
      }
      setUploads((prev) => prev.filter((u) => !selectedUploads.has(u.id)));
      setSelectedUploads(new Set());
    } catch (e) {
      console.error(e);
    } finally {
      setBulkDeleting(false);
    }
  }

  async function openUpload(upload) {
    setViewUpload(upload);
    setViewLoading(true);
    setViewRecords([]);
    try {
      const data = await apiFetch(`/records?upload_id=${upload.id}&limit=500`);
      setViewRecords(data.records || []);
    } catch (e) {
      console.error(e);
    } finally {
      setViewLoading(false);
    }
  }

  async function renameUpload(u) {
    const current = bsiUploadTitle(u);
    const next = window.prompt(
      'Statement title (standard: BSI · Carrier · MM-YYYY).\nLeave blank to reset to the automatic title.',
      current
    );
    if (next === null) return;
    setRenamingId(u.id);
    setError('');
    try {
      const r = await apiFetch(`/files/uploads/${u.id}/title`, {
        method: 'PATCH',
        body: JSON.stringify({ title: next }),
      });
      setUploads((prev) => prev.map((x) => (x.id === u.id
        ? { ...x, display_title: r.display_title, display_title_source: r.display_title_source }
        : x)));
    } catch (e) {
      setError(e.message || 'Rename failed');
    } finally {
      setRenamingId(null);
    }
  }

  async function exportUpload(u) {
    if (!u?.id) return;
    setExportingId(u.id);
    setError('');
    try {
      await exportUploadFile({
        path: commissionUploadExportPath(u.id, 'xlsx'),
        fallbackName: `${String(bsiUploadTitle(u) || u.original_name || 'bsi_statement').replace(/\.[^.]+$/, '').replace(/\s*·\s*/g, '_').replace(/(\d{2})\.(\d{4})/g, '$1-$2').replace(/\s+/g, '_')}_export.xlsx`,
      });
    } catch (e) {
      setError(e.message || 'Export failed');
    } finally {
      setExportingId(null);
    }
  }

  const q = searchQuery.trim().toLowerCase();
  const allCarriers = Array.from(new Set(
    uploads.flatMap((u) => (u.carrier || '').split(',').map((s) => s.trim()).filter(Boolean))
  )).sort();
  const visibleUploads = uploads
    .filter((u) => {
      if (q) {
        const hay = [
          bsiUploadTitle(u),
          statementSearchText(u.original_name, { category: 'bsi_statement' }),
          u.uploaded_by_name,
          u.carrier,
          u.uploaded_by != null ? `User ${u.uploaded_by}` : '',
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filterCarrier && !((u.carrier || '').toLowerCase().includes(filterCarrier.toLowerCase()))) return false;
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'date_asc':
          return new Date(a.uploaded_at) - new Date(b.uploaded_at);
        case 'carrier':
          return (a.carrier || '').localeCompare(b.carrier || '');
        case 'title':
          return compareBsiUploadsByTitle(a, b);
        case 'date_desc':
        default:
          return new Date(b.uploaded_at) - new Date(a.uploaded_at);
      }
    });

  return (
    <div>
      {routeConfirm && (
        <UploadRouteConfirm
          filename={routeConfirm.file?.name}
          detected={routeConfirm.detected}
          currentLabel="BSI Statements"
          onUseSuggested={() => {
            const { file, detected } = routeConfirm;
            setRouteConfirm(null);
            const pageId = UPLOAD_PAGE_BY_DEST[detected.id];
            if (pageId && onNavigate) {
              setPendingUpload(file);
              onNavigate(pageId);
            } else {
              handleFile(file);
            }
          }}
          onStayHere={() => {
            const { file } = routeConfirm;
            setRouteConfirm(null);
            handleFile(file);
          }}
          onCancel={() => setRouteConfirm(null)}
        />
      )}

      <UploadPageShell
        title="BSI Statements"
        subtitle="Carrier→BSI feeds (Humana / UHC / Aetna / Devoted). BSI→THE remittance belongs under Commission Statements."
      >
        <UploadDropZone
          dragOver={dragOver}
          setDragOver={setDragOver}
          onDropFiles={(files) => { if (files[0]) queueFile(files[0]); }}
          uploading={uploading}
          dropTitle="Drop BSI statement here"
          dropHint="Supports .xlsx, .xls, .csv, .pdf"
          accept=".xlsx,.xls,.csv,.pdf"
          inputId="bsi-file-input"
        />

        {error && <UploadAlert>{error}</UploadAlert>}

        {uploadResult && (
          <UploadAlert kind="success" title="Upload complete">
            {uploadResult.title && <div style={{ fontWeight: 600 }}>{uploadResult.title}</div>}
            File: {uploadResult.filename}
            {(uploadResult.rowCount != null || uploadResult.recordsImported != null) && (
              <div style={{ marginTop: 4 }}>
                Records imported: {uploadResult.rowCount ?? uploadResult.recordsImported}
              </div>
            )}
            {uploadResult.commissionSum != null && (
              <div style={{ marginTop: 4 }}>
                Statement commission: ${Number(uploadResult.commissionSum).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            )}
            {uploadResult.payeeFilter && uploadResult.payeeFilter.skippedPayeeCount > 0 && (
              <div style={{ marginTop: 4 }}>
                Skipped {uploadResult.payeeFilter.skippedPayeeCount} writing-agent payee row(s) (not BSI / THEI / Alba)
                {uploadResult.payeeFilter.hraConflicts && uploadResult.payeeFilter.hraConflicts.length > 0
                  ? ` — ${uploadResult.payeeFilter.hraConflicts.length} HRA/bonus row(s) paid to another agent were not assigned to Lina`
                  : ''}
              </div>
            )}
            {uploadResult.reconciliation && uploadResult.reconciliation.checked && (
              <div style={{ marginTop: 4, fontWeight: uploadResult.reconciliation.ok ? 400 : 600, color: uploadResult.reconciliation.ok ? undefined : '#b91c1c' }}>
                {uploadResult.reconciliation.ok
                  ? `Ties to carrier Commission Summary ($${Number(uploadResult.reconciliation.summaryTotal).toFixed(2)})` +
                    (Number(uploadResult.reconciliation.excludedFees) ? ` — BSI licensing fees of $${Number(uploadResult.reconciliation.excludedFees).toFixed(2)} not imported` : '')
                  : `Does NOT tie to carrier Commission Summary: imported $${Number(uploadResult.reconciliation.importedTotal).toFixed(2)} vs summary $${Number(uploadResult.reconciliation.summaryTotal).toFixed(2)} (variance $${Number(uploadResult.reconciliation.variance).toFixed(2)}). Some rows were dropped or misread — check before using this upload.`}
              </div>
            )}
            {uploadResult.carrierWarning && (
              <div style={{ marginTop: 4, fontWeight: 600, color: '#b91c1c' }}>
                {uploadResult.carrierWarning}
              </div>
            )}
            {uploadResult.missingPeriodWarning && (
              <div style={{ marginTop: 4, fontWeight: 600, color: '#b91c1c' }}>
                ⚠ {formatPeriodsInText(uploadResult.missingPeriodWarning.message)}
              </div>
            )}
            {uploadResult.unsplitWarning && (
              <div style={{ marginTop: 4, fontWeight: 600, color: '#b45309' }}>
                ⚠ {formatPeriodsInText(uploadResult.unsplitWarning.message)}
              </div>
            )}
            {uploadResult.chargebackWarning && (
              <div style={{ marginTop: 4, fontWeight: 600, color: '#b45309' }}>
                ⚠ {formatPeriodsInText(uploadResult.chargebackWarning.message)}
                <ChargebackFlagTable rows={uploadResult.chargebackFlags} />
              </div>
            )}
            {uploadResult.internalDuplicatesRemoved > 0 && (
              <div style={{ marginTop: 4 }}>
                Skipped {uploadResult.internalDuplicatesRemoved} exact duplicate line
                {uploadResult.internalDuplicatesRemoved === 1 ? '' : 's'} within this file
              </div>
            )}
            {uploadResult.resolvedRenewalsCount > 0 && (
              <div style={{ marginTop: 8, fontWeight: 500 }}>
                {uploadResult.resolvedRenewalsCount} chased/pending renewal
                {uploadResult.resolvedRenewalsCount === 1 ? '' : 's'} auto-resolved
              </div>
            )}
          </UploadAlert>
        )}

        <ChargebackCheckPanel />

        {uploads.length > 0 && (
          <UploadListToolbar
            search={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search BSI, filename, carrier, or uploader…"
            filterValue={filterCarrier}
            onFilterChange={setFilterCarrier}
            filterOptions={allCarriers}
            sortValue={sortBy}
            onSortChange={setSortBy}
            sortOptions={[
              { value: 'date_desc', label: '↓ Newest first' },
              { value: 'date_asc', label: '↑ Oldest first' },
              { value: 'carrier', label: 'Sort by carrier' },
              { value: 'title', label: 'Sort by title (carrier, month)' },
            ]}
            showClear={!!(searchQuery || filterCarrier)}
            onClear={() => { setSearchQuery(''); setFilterCarrier(''); }}
          />
        )}
        <UploadRowList
          items={visibleUploads}
          getKey={(u) => u.id}
          selectable
          selected={selectedUploads}
          onToggleSelect={toggleSelectUpload}
          onSelectAll={selectAllUploads}
          allSelected={selectedUploads.size === visibleUploads.length && visibleUploads.length > 0}
          headerLabel={`Uploaded BSI Statements ${q || filterCarrier ? `(${visibleUploads.length} of ${uploads.length})` : `(${uploads.length})`}`}
          bulkActions={user.role === 'admin' && selectedUploads.size > 0 ? (
            <button onClick={bulkDeleteUploads} disabled={bulkDeleting} style={{ background: '#E24B4A', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {bulkDeleting ? 'Deleting...' : `Delete ${selectedUploads.size} selected`}
            </button>
          ) : null}
          icon="🧾"
          hasAnyItems={uploads.length > 0}
          emptyState={(
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
              No BSI statements uploaded yet
            </div>
          )}
          noMatchState={(
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
              No uploads match “{searchQuery.trim()}”
            </div>
          )}
          renderPrimary={(u) => (
            <button onClick={() => openUpload(u)} style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              fontWeight: 600, fontSize: 13, color: 'var(--text)', textAlign: 'left',
            }}>
              <BsiTitle upload={u} />
            </button>
          )}
          renderMeta={(u) => `${u.row_count || 0} records · ${fmt(u.commission_sum)} · ${u.carrier || '—'} · ${formatDateTime(u.uploaded_at)} · by ${u.uploaded_by_name || `User ${u.uploaded_by}`}`}
          renderActions={(u) => (
            <>
              {user.role === 'admin' && (
                <button
                  onClick={() => renameUpload(u)}
                  disabled={renamingId === u.id}
                  title="Edit title"
                  style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer', color: 'var(--text)' }}
                >
                  {renamingId === u.id ? '...' : 'Rename'}
                </button>
              )}
              <button
                onClick={() => exportUpload(u)}
                disabled={exportingId === u.id}
                style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer', color: 'var(--text)' }}
              >
                {exportingId === u.id ? '...' : 'Export'}
              </button>
              {user.role === 'admin' && (
                <button onClick={() => deleteUpload(u.id)} disabled={deletingId === u.id}
                  style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer', color: '#E24B4A' }}>
                  {deletingId === u.id ? '...' : 'Delete'}
                </button>
              )}
            </>
          )}
        />
      </UploadPageShell>

      {/* Record preview — shared React modal (replaces the old innerHTML modal) */}
      <UploadRecordPreviewModal
        open={!!viewUpload}
        onClose={() => setViewUpload(null)}
        title={viewUpload ? <BsiTitle upload={viewUpload} /> : ''}
        subtitle={viewUpload ? `${viewUpload.row_count || 0} records · ${fmt(viewUpload.commission_sum)} · ${viewUpload.carrier || '—'} · ${formatDateTime(viewUpload.uploaded_at)}` : ''}
        loading={viewLoading}
        rows={viewRecords}
        columns={[
          { key: 'agent_name', header: 'Agent', render: (r) => r.agent_name || '—' },
          { key: 'client_full_name', header: 'Client', render: (r) => r.client_full_name || '—' },
          { key: 'carrier', header: 'Carrier', render: (r) => r.carrier || '—' },
          { key: 'plan_type', header: 'Plan', render: (r) => r.plan_type || '—' },
          { key: 'effective_date', header: 'Eff. Date', render: (r) => r.effective_date || '—' },
          { key: 'payment_period', header: 'Period', render: (r) => r.payment_period || '—' },
          {
            key: 'classification', header: 'Classification',
            render: (r) => {
              const cls = r.classification;
              const bg = cls === 'New Business' ? '#c6f6d5' : cls === 'Renewal' ? '#bee3f8' : cls === 'Chargeback' ? '#fed7d7' : cls === 'Held' ? '#fefcbf' : '#e2e8f0';
              return <span style={{ background: bg, padding: '2px 6px', borderRadius: 3, fontSize: 11 }}>{cls || '—'}</span>;
            },
          },
          {
            key: 'commission', header: 'Commission', align: 'right',
            render: (r) => <span style={{ fontWeight: 500, color: parseFloat(r.commission) < 0 ? '#e53e3e' : '#2f855a' }}>{fmt(r.commission)}</span>,
          },
        ]}
        footer={(rows) => (
          <>
            <td colSpan={6} style={{ padding: '8px 12px', fontSize: 12 }}>Total ({rows.length} records)</td>
            <td style={{ padding: '8px 12px', fontSize: 12, textAlign: 'right', color: '#1D9E75' }}>
              {fmt(rows.reduce((s, r) => s + (parseFloat(r.commission) || 0), 0))}
            </td>
          </>
        )}
      />
    </div>
  );
}

/** Standardized title on top, original filename underneath. */
function BsiTitle({ upload }) {
  const title = bsiUploadTitle(upload);
  const name = String(upload?.original_name || '');
  return (
    <div>
      <div style={{ fontWeight: 600 }}>{title || name || '—'}</div>
      {name && name !== title && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400, marginTop: 2, wordBreak: 'break-word' }}>
          {name}
        </div>
      )}
    </div>
  );
}
